import { Injectable } from "@angular/core";
import { select } from "@ngrx/store";
import { FetchStatusSelector } from "@obg/ngrx-utils";
import { RouteSelector } from "@obg/ngx.router";
import { asyncScheduler, combineLatest, Observable } from "rxjs";
import { debounceTime, filter, map, observeOn, switchMap, take } from "rxjs/operators";

import { AUTH_ROUTE } from "../auth/auth.const";
import { AuthenticationStatus } from "../auth/auth.model";
import { AuthSelector } from "../auth/auth.selector";
import { JurisdictionSelector } from "../jurisdiction/jurisdiction.selector";
import { Message, MessageFilter } from "../message/message.model";
import { MessageSelector } from "../message/message.selector";
import { CORE_ROUTE } from "../route";
import { CoreState } from "../state";

import { ACTION_PREFIX } from "./responsible-gaming.action";
import { ResponsibleGamingConfigService } from "./responsible-gaming.config";
import { ResponsibleGamingState } from "./responsible-gaming.model";

/**
 * @deprecated [fabric-deprecated] - Use ResponsibleGamingService from app-responsible-gaming instead
 */
@Injectable({
	providedIn: "root",
})
export class ResponsibleGamingSelector {
	constructor(
		private configService: ResponsibleGamingConfigService,
		private messageSelector: MessageSelector
	) {}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.GetSessionDuration instead
	 */
	getState() {
		return (state: CoreState): ResponsibleGamingState => state.responsibleGaming;
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.GetSessionDuration instead
	 */
	getSessionDuration() {
		return (state: CoreState): number => state.responsibleGaming.sessionDurationMinutes;
	}

	getAsaActive$() {
		return (state$: Observable<CoreState>): Observable<boolean> =>
			state$.pipe(
				select(AuthSelector.getAuthenticationStatus),
				filter((status) => status !== AuthenticationStatus.Pending),
				switchMap((status) =>
					state$.pipe(
						select(JurisdictionSelector.get),
						// Double observeOn is required as config service is not updated
						// within the first group of sync code.
						// TODO: https://jirasson.betsson.local/browse/OBGD-3188
						observeOn(asyncScheduler),
						observeOn(asyncScheduler),
						map(
							() => this.configService.getAsaEnabled() && status === AuthenticationStatus.NotAuthenticated
						)
					)
				)
			);
	}

	isBusy() {
		return (state: CoreState): boolean => FetchStatusSelector.isBusy(ACTION_PREFIX)(state);
	}

	postLoginMessage$(messageFilter: MessageFilter) {
		const ignoredRoutes = [
			AUTH_ROUTE.login,
			CORE_ROUTE.registration.register,
			CORE_ROUTE.registration.registerSuccess,
		];

		return (state$: Observable<CoreState>): Observable<Message> =>
			state$.pipe(
				select(this.messageSelector.getFirst(messageFilter)),
				filter<Message>((message) => !!message),
				observeOn(asyncScheduler),
				switchMap((message) =>
					combineLatest([
						state$.pipe(select(RouteSelector.isNavigating)),
						state$.pipe(select(RouteSelector.getCurrent)),
					]).pipe(
						// wait until navigating or on ignored route
						filter(
							([isNavigating, current]) =>
								!isNavigating && !!current && !ignoredRoutes.includes(current.name)
						),
						debounceTime(1000),
						take(1),
						map(() => message)
					)
				)
			);
	}
}
